import { useEffect, useRef, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { blogHref, docsHref } from "@/lib/routes";
import { useIsLoggedInAfterMount } from "@/store/auth";
import { useScrollReveal } from "@/lib/scrollReveal";
import { LogoMark } from "@/components/landing/LogoMark";
import { SiteFooter } from "@/components/landing/SiteFooter";
import { DashboardLink } from "@/components/landing/DashboardLink";
import { AuthEmulatorRedirectResume } from "@/components/auth/AuthEmulatorRedirectResume";
import { SITE } from "@/content/marketing";
import { pushCtaClick } from "@/lib/gtm";
import { useWaitlist, WaitlistContext } from "./-home.context";

// Loaded on first open (and warmed during idle time after hydration, below):
// the dialog is the whole signup/login surface and none of it is visible on
// the landing page's first paint — see `-home.waitlist-dialog.tsx`.
//
// Loaded by hand into state rather than through `React.lazy`: `lazy` rethrows
// a failed chunk fetch during render, and with no error boundary between the
// layout and the root route's `errorComponent` a flaky connection (or a deploy
// rotating the chunk hash under an open tab) would replace the whole landing
// page with the 500 page — and `lazy` caches the rejection, so no later tap
// could recover. Here a failure closes the dialog, says so in a toast, and the
// next tap simply tries again.
const loadWaitlistDialog = () => import("./-home.waitlist-dialog");
type WaitlistDialogComponent = Awaited<
  ReturnType<typeof loadWaitlistDialog>
>["WaitlistDialog"];

export const Route = createFileRoute("/_home")({
  // A child route can't be "more SSR" than its parent, so the layout must
  // opt in for /, /faq, /pricing to server-render (D-216-5). Children that
  // don't set `ssr` themselves still fall back to `defaultSsr: false`.
  ssr: true,
  component: HomeLayout,
});

function HomeLayout() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  // Once loaded, the dialog stays mounted (closed) so its close animation and
  // the `view` reset on reopen behave exactly as the always-mounted version did.
  const [WaitlistDialog, setWaitlistDialog] =
    useState<WaitlistDialogComponent | null>(null);
  // `open` is true while the chunk is still on its way, so the CTAs can show
  // a pending state (`WaitlistContext.pending`) instead of doing nothing for
  // the round-trip — usually zero, because the idle warm-up below has already
  // fetched it, but on a tap within the first seconds of a slow-4G load it is
  // the only feedback the user gets.
  const loadingRef = useRef(false);
  const onStartFree = () => {
    setWaitlistOpen(true);
    if (WaitlistDialog || loadingRef.current) return;
    loadingRef.current = true;
    loadWaitlistDialog().then(
      (m) => {
        loadingRef.current = false;
        setWaitlistDialog(() => m.WaitlistDialog);
      },
      () => {
        loadingRef.current = false;
        setWaitlistOpen(false);
        toast.error("Couldn't load sign-in. Check your connection and try again.");
      },
    );
  };
  const siteRef = useRef<HTMLDivElement>(null);
  useScrollReveal(siteRef);

  // Warm the dialog chunk once the page is idle, so the first tap on the CTA
  // does not wait a round-trip on a slow connection. Idle, not on mount: the
  // point of the lazy split is that this fetch never competes with the
  // hero's CSS and the hydration bundle. `requestIdleCallback` is absent in
  // Safari — fall back to a short timeout there. A failed warm-up is
  // swallowed: the user did nothing, and the open path reports its own
  // failure.
  useEffect(() => {
    const warm = () => {
      loadWaitlistDialog().catch(() => {});
    };
    if (typeof requestIdleCallback === "function") {
      const id = requestIdleCallback(warm, { timeout: 3000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 1500);
    return () => clearTimeout(id);
  }, []);

  return (
    <WaitlistContext.Provider
      value={{ onStartFree, pending: waitlistOpen && !WaitlistDialog }}
    >
      <div ref={siteRef} className="gs-site flex min-h-screen flex-col">
        <Nav />
        {/*
          `flex flex-col`, not a bare `flex-1`: this div is what grows, but the
          aux routes centre their card with `<main className="flex flex-1 …">`,
          and `flex-1` is inert inside a block box — `main` would collapse to
          content height and the login / account-deleted cards would sit
          top-aligned instead of centred (#792 review).
        */}
        <div className="flex flex-1 flex-col">
          <Outlet />
        </div>
        <SiteFooter />
        {/* Cursor's built-in browser: finish Firebase Auth emulator redirect sign-in. */}
        {import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR !== "false" ? (
          <AuthEmulatorRedirectResume />
        ) : null}
        {WaitlistDialog ? (
          <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} />
        ) : null}
      </div>
    </WaitlistContext.Provider>
  );
}

/*
 * The header's link list, rendered once per nav (the inline `lg:flex` one and
 * the phone menu). One component rather than two hand-kept lists: an entry
 * present in one nav and missing from the other is exactly the defect the
 * 2026-08-28 mobile fix repaired, so the list exists in one place (#900
 * review). Blog and Docs stay hardcoded here: their hrefs come from
 * `lib/routes.ts`, not authored copy. Every `SITE.nav` entry is a page link
 * now — the "How it works" section-anchor entry (and its `hash` branch) was
 * removed 2026-08-28; resurrect the branch from history if an anchor entry
 * ever returns.
 */
export function NavLinks({
  linkClassName,
  onNavigate,
}: {
  linkClassName: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      {SITE.nav.map((item) => (
        <Link
          key={item.label}
          to={item.to}
          className={linkClassName}
          onClick={onNavigate}
        >
          {item.label}
        </Link>
      ))}
      <a
        href={blogHref()}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
        onClick={onNavigate}
      >
        Blog
      </a>
      <a
        href={docsHref()}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
        onClick={onNavigate}
      >
        Docs
      </a>
    </>
  );
}

function Nav() {
  const { onStartFree, pending } = useWaitlist();
  const showDashboard = useIsLoggedInAfterMount();
  /*
   * Below `lg` the page links live behind a disclosure instead of being
   * dropped (the retired `collapse` scheme hid Platform and Agents outright,
   * leaving the footer as their only mobile path — product feedback,
   * 2026-08-28). The CTA stays in the bar at every width and is rendered
   * exactly once: `DashboardLink announce` must not gain a second,
   * CSS-hidden instance, because `display: none` does not silence a live
   * region's insertions.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  /*
   * `_home` is a persistent layout, so a navigation that bypasses the menu's
   * own links — the wordmark, browser back/forward — swaps the child route
   * without remounting `Nav`, and the open menu would ride along onto the
   * destination (#900 review). Closing on every pathname change covers those;
   * the per-link `onNavigate` close stays for the case this effect cannot
   * see, a tap on the link to the route already on screen.
   */
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const navLink =
    "text-[length:var(--gs-text-nav)] text-[color:var(--gs-muted)] no-underline hover:text-[color:var(--gs-text)] transition-colors";

  return (
    <header className="gs-marketing-header mx-auto flex w-full max-w-[1080px] flex-wrap items-center justify-between gap-x-4 px-6 py-5">
      <Link
        to="/"
        className="flex items-center gap-2.5 no-underline text-[color:var(--gs-text)] font-semibold text-[length:var(--gs-text-title)] [font-family:var(--gs-font-display)]"
      >
        <LogoMark />
        {SITE.wordmark}
      </Link>
      {/*
        `gap-2` below `lg`, and the number is load-bearing: at 390px the
        header's content box is 342px and brand (169) + header gap (16) +
        this group must fit inside it. With the 44px trigger the old `gap-4`
        put the sum at exactly 342 and fractional glyph widths tipped it onto
        a second row (measured). The 44px trigger's icon is inset 12px, so
        the visual gap to the CTA stays ~20px.
      */}
      <div className="flex items-center gap-2 lg:gap-6">
        {/*
          Two `aria-label="Site"` navs, deliberately: only one is ever in the
          accessibility tree (the other is `display: none` at every width), so
          the label never names two live landmarks at once — it names the same
          landmark in its two dressings, alongside the footer's "Footer".
        */}
        <nav aria-label="Site" className="hidden items-center gap-6 lg:flex">
          <NavLinks linkClassName={navLink} />
        </nav>
        {showDashboard ? (
          // The one instance that announces. `Hero` and `FinalCta` render a
          // `DashboardLink` too (via `StartFreeCta`), and all three flip
          // together — see the component for why only one may carry the live
          // region. This is the instance present on every `_home` route.
          <DashboardLink announce />
        ) : (
          <button
            type="button"
            data-variant="primary"
            aria-busy={pending || undefined}
            disabled={pending}
            onClick={() => {
              pushCtaClick({ ctaLocation: "nav", ctaLabel: SITE.ctaPrimary });
              onStartFree();
            }}
          >
            {SITE.ctaPrimary}
          </button>
        )}
        {/*
          `size-11` (44px), the touch-target size: the landing page is outside
          `.gs-app`/`.gs-tokens`, so `theme.css`'s coarse-pointer floor cannot
          reach this button and the size is stated here (#900 review).
          `-mr-3` cancels the icon's 12px inset so the glyph sits flush with
          the content edge, where the old text links ended.
        */}
        <button
          type="button"
          className="-mr-3 inline-flex size-11 items-center justify-center text-[color:var(--gs-text)] lg:hidden"
          aria-expanded={menuOpen}
          aria-controls="site-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? (
            <X className="size-5" aria-hidden />
          ) : (
            <Menu className="size-5" aria-hidden />
          )}
        </button>
      </div>
      {/*
        Always rendered, toggled with the `hidden` attribute rather than
        conditional mounting, so the trigger's `aria-controls` resolves at
        every moment (#900 review). Tailwind's preflight keeps `[hidden]`
        `display: none !important`, so the attribute wins over the `flex`
        utility; `display: none` also keeps the closed menu's links out of
        the accessibility tree and the tab order.
      */}
      <nav
        id="site-menu"
        aria-label="Site"
        hidden={!menuOpen}
        className="flex w-full flex-col pt-2 lg:hidden"
      >
        <NavLinks linkClassName={`${navLink} py-2.5`} onNavigate={closeMenu} />
      </nav>
    </header>
  );
}
